import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EventManagerPanel } from "spark-core-dx";
import SearchEntityObject from "../../domains/convoke/screens/SearchEntityObject";
import EDIDiagnosticsManage from "../../domains/convoke/screens/EDIDiagnosticsManage";
import MigrationMappingDraftManage from "../../domains/convoke/screens/MigrationMappingDraftManage";
import MigrationProjectManage from "../../domains/convoke/screens/MigrationProjectManage";
import MarketplaceResourcePurchasePanel from "../../domains/marketplace/components/MarketplaceResourcePurchasePanel";
import PermissionSetManage from "../../domains/convoke/screens/PermissionSetManage";
import SourceColumnSetManage from "../../domains/convoke/screens/SourceColumnSetManage";
import SearchSourceObject from "../../domains/convoke/screens/SearchSourceObject";
import SearchWarehouse from "../../domains/convoke/screens/SearchWarehouse";
import SearchWarehouseView from "../../domains/convoke/screens/SearchWarehouseView";
import {
  AI_PANEL_COMMAND_EVENT,
  AI_PANEL_GRID_REFRESH_EVENT,
  buildAiPanelSearchParams,
  clearAiPanelSearchParams,
  readAiPanelDescriptorFromSearch
} from "./aiPanelCommand";

const toSearchString = (params) => {
  const text = params.toString();
  return text ? `?${text}` : "";
};

const PANEL_TRANSITION_MS = 240;

const AIPanelCommandHost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const descriptor = useMemo(
    () => readAiPanelDescriptorFromSearch(location.search),
    [location.search]
  );
  const descriptorKey = useMemo(
    () => descriptor
      ? [
        descriptor.panel,
        descriptor.route,
            descriptor.id,
            descriptor.referenceId,
            descriptor.sessionId,
            JSON.stringify(descriptor.params ?? {})
          ].join("|")
          : "",
    [descriptor]
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const openFrameRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const handleOpenPanel = (event) => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
        setPanelOpen(true);
      }
      const nextParams = buildAiPanelSearchParams(location.search, event.detail);
      navigate(
        {
          pathname: location.pathname,
          search: toSearchString(nextParams),
          hash: location.hash
        },
        { replace: false }
      );
    };

    window.addEventListener(AI_PANEL_COMMAND_EVENT, handleOpenPanel);
    return () => window.removeEventListener(AI_PANEL_COMMAND_EVENT, handleOpenPanel);
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (openFrameRef.current) {
      window.cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }

    if (!descriptor) {
      setPanelOpen(false);
      return undefined;
    }

    setPanelOpen(false);
    openFrameRef.current = window.requestAnimationFrame(() => {
      openFrameRef.current = window.requestAnimationFrame(() => {
        setPanelOpen(true);
        openFrameRef.current = null;
      });
    });

    return () => {
      if (openFrameRef.current) {
        window.cancelAnimationFrame(openFrameRef.current);
        openFrameRef.current = null;
      }
    };
  }, [descriptor, descriptorKey]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  const refreshGridAfterPanelClose = useCallback((closedDescriptor, targetUrl) => {
    const panelRefreshConfig = {
      permissionset: {
        itemName: "PermissionSet",
        fallbackReload: true
      },
      sourceobjectmanage: {
        itemName: "SourceObject",
        fallbackReload: false
      },
      marketplaceaddresource: {
        itemName: "ResourceConfigurationWizard",
        fallbackReload: false
      }
    }[closedDescriptor?.panel];

    if (!panelRefreshConfig) {
      return;
    }

    const refreshEvent = new CustomEvent(AI_PANEL_GRID_REFRESH_EVENT, {
      cancelable: true,
      detail: {
        panel: closedDescriptor.panel,
        itemName: panelRefreshConfig.itemName,
        id: closedDescriptor.id,
        sourceObjectId: closedDescriptor.panel === "sourceobjectmanage" ? closedDescriptor.id : undefined,
        sourceSystemId: closedDescriptor.params?.sourceSystemId || closedDescriptor.params?.SourceSystemId || "",
        productResourceKey: closedDescriptor.params?.productResourceKey || closedDescriptor.params?.ProductResourceKey || closedDescriptor.id || "",
        clientResourceId: closedDescriptor.params?.clientResourceId || closedDescriptor.params?.ClientResourceId || "",
        resourceConfigurationId: closedDescriptor.params?.resourceConfigurationId || closedDescriptor.params?.ResourceConfigurationId || "",
        targetUrl,
        reason: "panel-close"
      }
    });
    const wasNotHandled = window.dispatchEvent(refreshEvent);
    if (panelRefreshConfig.fallbackReload && wasNotHandled && targetUrl) {
      window.setTimeout(() => {
        window.location.assign(targetUrl);
      }, 0);
    }
  }, []);

  const handleClose = () => {
    const closedDescriptor = descriptor;
    setPanelOpen(false);
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      const nextParams = clearAiPanelSearchParams(location.search);
      const nextSearch = toSearchString(nextParams);
      const targetUrl = `${location.pathname}${nextSearch}${location.hash}`;
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
          hash: location.hash
        },
        { replace: false }
      );
      refreshGridAfterPanelClose(closedDescriptor, targetUrl);
    }, PANEL_TRANSITION_MS);
  };

  if (!descriptor) {
    return null;
  }

  if (descriptor.panel === "whsearch") {
    return (
      <SearchWarehouse
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelReferenceId={descriptor.referenceId}
        panelSessionId={descriptor.sessionId}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "sosearch") {
    return (
      <SearchSourceObject
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "sourceobjectmanage") {
    return (
      <SourceColumnSetManage
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "marketplaceaddresource") {
    return (
      <MarketplaceResourcePurchasePanel
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "eosearch") {
    return (
      <SearchEntityObject
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelReferenceId={descriptor.referenceId}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "warehouseviewsearch") {
    return (
      <SearchWarehouseView
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelReferenceId={descriptor.referenceId}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "migration") {
    return (
      <MigrationProjectManage
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "migrationdraft") {
    return (
      <MigrationMappingDraftManage
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "permissionset") {
    return (
      <PermissionSetManage
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "edidiagnostics") {
    return (
      <EDIDiagnosticsManage
        key={descriptorKey}
        panelMode
        panelOpen={panelOpen}
        panelId={descriptor.id}
        panelSessionId={descriptor.sessionId}
        panelParams={descriptor.params}
        onPanelClose={handleClose}
      />
    );
  }

  if (descriptor.panel === "event") {
    return (
      <EventManagerPanel
        key={descriptorKey}
        panelOpen={panelOpen}
        panelId={descriptor.id}
        onPanelClose={handleClose}
      />
    );
  }

  return null;
};

export default AIPanelCommandHost;
